import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import colegioApiService from '../services/ColegioApiService';
import LoadingSpinner from './common/LoadingSpinner';
import TableExportActions from './common/TableExportActions';
import { isAdminColegio } from '../utils/adminMenu';
import useMetricsRealtime from '../hooks/useMetricsRealtime';
import './AdminColegioStats.css';

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayRange = () => {
  const now = new Date();
  const value = formatDateInput(now);
  return { from: value, to: value };
};

const getMonthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: formatDateInput(from), to: formatDateInput(now) };
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const AdminColegioStats = () => {
  const { colegioId } = useParams();
  const navigate = useNavigate();
  const { user, logout, isLoading: authLoading } = useAuth();
  const [selectedPreset, setSelectedPreset] = useState('month');
  const [filters, setFilters] = useState(() => getMonthRange());
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');

  const isGeneralAdmin = useMemo(() => !isAdminColegio(user), [user]);
  const roleLabel = user?.roleName || user?.nombreRol || user?.role || 'Admin';
  const colegioNombre = stats?.colegioNombre || 'Colegio';
  const totalRoles = Array.isArray(stats?.resumenPorRol) ? stats.resumenPorRol.length : 0;
  const totalEventos = Number(stats?.totalEventos || 0);
  const totalPageViews = Number(stats?.totalPageViews || 0);
  const totalChatsStarted = Number(stats?.totalChatsStarted || 0);
  const totalAssistantsOpened = Number(stats?.totalAssistantsOpened || 0);
  const totalBoardsStarted = Number(stats?.totalBoardsStarted || 0);
  const totalCallsStarted = Number(stats?.totalCallsStarted || 0);
  const totalForumReplies = Number(stats?.totalForumReplies || 0);
  const totalFilesDownloaded = Number(stats?.totalFilesDownloaded || 0);
  const totalSolicitudesCreacion = Number(stats?.totalSolicitudesCreacion || 0);
  const totalSolicitudesEdicion = Number(stats?.totalSolicitudesEdicion || 0);
  const totalEventosListados = Array.isArray(stats?.eventos) ? stats.eventos.length : 0;

  const loadStats = async ({ nextFilters = filters, showLoader = true, silentError = false } = {}) => {
    if (!colegioId) return;
    if (showLoader) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    if (!silentError) {
      setError('');
    }
    try {
      const response = await colegioApiService.getStatsByColegio(colegioId, nextFilters);
      setStats(response);
      if (!silentError) {
        setError('');
      }
      return response;
    } catch (err) {
      if (!silentError) {
        setError(err.message || 'No pudimos cargar las estadísticas del colegio.');
        setStats(null);
      } else {
        console.warn('No se pudieron refrescar las métricas del colegio en vivo', err);
      }
      return null;
    } finally {
      if (showLoader) {
        setIsLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  const { highlightedCards, liveAlert } = useMetricsRealtime({
    enabled: !authLoading && isGeneralAdmin && Boolean(colegioId),
    scope: 'colegio',
    colegioId,
    stats,
    loadStats
  });

  useEffect(() => {
    if (authLoading) return;
    if (!colegioId) {
      setError('No pudimos identificar el colegio.');
      setIsLoading(false);
      return;
    }
    if (!isGeneralAdmin) {
      setError('No tienes permisos para acceder a esta sección.');
      setIsLoading(false);
      return;
    }
    loadStats({ nextFilters: filters });
  }, [authLoading, colegioId, isGeneralAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (preset) => {
    const nextFilters = preset === 'today' ? getTodayRange() : getMonthRange();
    setSelectedPreset(preset);
    setFilters(nextFilters);
    loadStats({ nextFilters });
  };

  const handleCustomFilterChange = (field, value) => {
    setSelectedPreset('custom');
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyCustomRange = () => {
    loadStats({ nextFilters: filters });
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (authLoading || isLoading) {
    return (
      <div className="colegio-stats-page">
        <div className="colegio-stats-loading">
          <LoadingSpinner message="Cargando estadísticas del colegio..." />
        </div>
      </div>
    );
  }

  return (
    <div className="colegio-stats-page">
      <div className="colegio-stats-shell">
        <header className="colegio-stats-header">
          <div>
            <p className="eyebrow">Estadísticas</p>
            <h1>Actividad del colegio</h1>
            <p className="subtitle">
              {colegioNombre}. Revisa logins y actividad del aula en el período seleccionado.
            </p>
          </div>
          <div className="colegio-stats-header-actions">
            <div className="colegio-stats-user-chip">
              <span>{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <small>{roleLabel}</small>
            </div>
            <button type="button" className="secondary-btn" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left" />
              <span>Volver</span>
            </button>
            <button type="button" className="logout-btn-header" onClick={handleLogout} title="Cerrar sesión">
              <i className="fas fa-sign-out-alt" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </header>

        <section className="colegio-stats-toolbar">
          <div className="colegio-stats-presets">
            <button
              type="button"
              className={selectedPreset === 'today' ? 'active' : ''}
              onClick={() => applyPreset('today')}
            >
              Hoy
            </button>
            <button
              type="button"
              className={selectedPreset === 'month' ? 'active' : ''}
              onClick={() => applyPreset('month')}
            >
              Mes actual
            </button>
          </div>
          <div className="colegio-stats-custom-range">
            <label>
              Desde
              <input
                type="date"
                value={filters.from}
                onChange={(e) => handleCustomFilterChange('from', e.target.value)}
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={filters.to}
                onChange={(e) => handleCustomFilterChange('to', e.target.value)}
              />
            </label>
            <button type="button" className="primary-btn" onClick={handleApplyCustomRange}>
              <i className="fas fa-filter" />
              <span>Aplicar rango</span>
            </button>
          </div>
        </section>

        {liveAlert && (
          <div className="colegio-stats-live-alert">
            <i className="fas fa-check-circle" />
            <span>{liveAlert.text}</span>
            {isRefreshing && <small>Sincronizando…</small>}
          </div>
        )}

        {error && (
          <div className="colegio-stats-error">
            <i className="fas fa-exclamation-triangle" />
            <span>{error}</span>
          </div>
        )}

        {!error && (
          <>
            <section className="colegio-stats-cards">
              <article className={`colegio-stats-card${highlightedCards.totalLogins ? ' is-updated' : ''}`}>
                <p>Total de logins</p>
                <strong>{Number(stats?.totalLogins || 0).toLocaleString('es-AR')}</strong>
                <span>{formatDateLabel(stats?.from)} al {formatDateLabel(stats?.to)}</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalPageViews ? ' is-updated' : ''}`}>
                <p>Cargas del aula</p>
                <strong>{totalPageViews.toLocaleString('es-AR')}</strong>
                <span>Visitas públicas registradas</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalChatsStarted ? ' is-updated' : ''}`}>
                <p>Chats iniciados</p>
                <strong>{totalChatsStarted.toLocaleString('es-AR')}</strong>
                <span>Chat grupal y asistentes desde el aula</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalAssistantsOpened ? ' is-updated' : ''}`}>
                <p>Asistentes abiertos</p>
                <strong>{totalAssistantsOpened.toLocaleString('es-AR')}</strong>
                <span>Aperturas de asistentes desde el aula</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalBoardsStarted ? ' is-updated' : ''}`}>
                <p>Pizarras iniciadas</p>
                <strong>{totalBoardsStarted.toLocaleString('es-AR')}</strong>
                <span>Sesiones de pizarra colaborativa</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalCallsStarted ? ' is-updated' : ''}`}>
                <p>Videollamadas iniciadas</p>
                <strong>{totalCallsStarted.toLocaleString('es-AR')}</strong>
                <span>Inicios de videollamada desde el aula</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalForumReplies ? ' is-updated' : ''}`}>
                <p>Respuestas en foros</p>
                <strong>{totalForumReplies.toLocaleString('es-AR')}</strong>
                <span>Respuestas publicadas en mensajes del aula</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalFilesDownloaded ? ' is-updated' : ''}`}>
                <p>Descargas de archivos</p>
                <strong>{totalFilesDownloaded.toLocaleString('es-AR')}</strong>
                <span>Descargas individuales y ZIP del aula</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalSolicitudesCreacion ? ' is-updated' : ''}`}>
                <p>Solicitudes de creación</p>
                <strong>{totalSolicitudesCreacion.toLocaleString('es-AR')}</strong>
                <span>Pedidos de creación de asistentes IA</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalSolicitudesEdicion ? ' is-updated' : ''}`}>
                <p>Solicitudes de edición</p>
                <strong>{totalSolicitudesEdicion.toLocaleString('es-AR')}</strong>
                <span>Cambios solicitados sobre asistentes IA</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalEventos ? ' is-updated' : ''}`}>
                <p>Eventos del período</p>
                <strong>{totalEventos.toLocaleString('es-AR')}</strong>
                <span>{totalEventosListados.toLocaleString('es-AR')} listados en el feed reciente</span>
              </article>
            </section>

            <section className="colegio-stats-grid">
              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Eventos por tipo</h2>
                  <p>Totales acumulados por actividad registrada.</p>
                  <TableExportActions
                    tableSelector="#admin-colegio-stats-eventos-tipo-table"
                    fileName="colegio-eventos-por-tipo"
                    title={`Eventos por tipo - ${colegioNombre}`}
                    compact
                    disabled={!Array.isArray(stats?.resumenPorTipoEvento) || stats.resumenPorTipoEvento.length === 0}
                  />
                </div>
                {Array.isArray(stats?.resumenPorTipoEvento) && stats.resumenPorTipoEvento.length > 0 ? (
                  <div className="panel-table-wrapper">
                    <table id="admin-colegio-stats-eventos-tipo-table" className="panel-table">
                      <thead>
                        <tr>
                          <th>Evento</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.resumenPorTipoEvento.map((item) => (
                          <tr key={item.tipoEvento}>
                            <td>{item.etiqueta || item.tipoEvento}</td>
                            <td className="text-right">{Number(item.total || 0).toLocaleString('es-AR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="panel-empty">No hay actividad registrada para el rango seleccionado.</div>
                )}
              </div>

              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Actividad por día</h2>
                  <p>Evolución diaria del total y detalle por tipo de evento.</p>
                </div>
                {Array.isArray(stats?.actividadPorDia) && stats.actividadPorDia.length > 0 ? (
                  <div className="activity-list">
                    {stats.actividadPorDia.map((item) => (
                      <div className="activity-row" key={item.fecha}>
                        <div>
                          <strong>{formatDateLabel(item.fecha)}</strong>
                          <p>{Number(item.total || 0).toLocaleString('es-AR')} eventos</p>
                        </div>
                        <div className="activity-tags">
                          {(item.porTipoEvento || []).map((evento) => (
                            <span className="activity-tag" key={`${item.fecha}-${evento.tipoEvento}`}>
                              {evento.etiqueta || evento.tipoEvento}: {Number(evento.total || 0).toLocaleString('es-AR')}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="panel-empty">No hay actividad diaria disponible.</div>
                )}
              </div>
            </section>

            <section className="colegio-stats-panel">
              <div className="panel-header">
                <h2>Logins por rol</h2>
                <p>Breakdown del evento de login según el rol capturado al momento del acceso.</p>
                <TableExportActions
                  tableSelector="#admin-colegio-stats-logins-rol-table"
                  fileName="colegio-logins-por-rol"
                  title={`Logins por rol - ${colegioNombre}`}
                  compact
                  disabled={!Array.isArray(stats?.resumenPorRol) || stats.resumenPorRol.length === 0}
                />
              </div>
              {Array.isArray(stats?.resumenPorRol) && stats.resumenPorRol.length > 0 ? (
                <div className="panel-table-wrapper">
                  <table id="admin-colegio-stats-logins-rol-table" className="panel-table">
                    <thead>
                      <tr>
                        <th>Rol</th>
                        <th className="text-right">Logins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.resumenPorRol.map((item) => (
                        <tr key={`${item.rolNormalizado}-${item.nombreRol}`}>
                          <td>{item.nombreRol}</td>
                          <td className="text-right">{Number(item.total || 0).toLocaleString('es-AR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="panel-empty">
                  {totalRoles === 0 ? 'No hay logins registrados para el rango seleccionado.' : 'No hay roles con logins en el rango.'}
                </div>
              )}
            </section>

            <section className="colegio-stats-panel">
              <div className="panel-header">
                <h2>Últimos eventos</h2>
                <p>Actividad reciente registrada para el colegio en el rango seleccionado.</p>
                <TableExportActions
                  tableSelector="#admin-colegio-stats-ultimos-eventos-table"
                  fileName="colegio-ultimos-eventos"
                  title={`Últimos eventos - ${colegioNombre}`}
                  compact
                  disabled={!Array.isArray(stats?.eventos) || stats.eventos.length === 0}
                />
              </div>
              {Array.isArray(stats?.eventos) && stats.eventos.length > 0 ? (
                <div className="panel-table-wrapper">
                  <table id="admin-colegio-stats-ultimos-eventos-table" className="panel-table">
                    <thead>
                      <tr>
                        <th>Fecha local</th>
                        <th>Evento</th>
                        <th>Usuario</th>
                        <th>Email</th>
                        <th>Rol</th>
                        <th>Detalle</th>
                        <th>Canal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.eventos.map((evento) => (
                        <tr key={evento.id}>
                          <td>{formatDateTime(evento.fechaLocal)}</td>
                          <td>{evento.etiqueta || evento.tipoEvento || 'Evento'}</td>
                          <td>{evento.nombreUsuario || 'Usuario desconocido'}</td>
                          <td>{evento.emailUsuario || '—'}</td>
                          <td>{evento.nombreRol || 'Sin rol'}</td>
                          <td>{evento.detalle || '—'}</td>
                          <td>{evento.canal || 'desconocido'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="panel-empty">No hay eventos recientes para mostrar.</div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminColegioStats;
